import React, { useState, useRef, useEffect } from 'react';
import ReactHowler from 'react-howler';
import { Play, Pause, Download, X } from 'lucide-react';
import { Card } from '../../ui/bootstrap';

interface RecordedAudioPlayerProps {
    audioUrl: string;
    fileName?: string;
    /** Shows a discard/remove button when provided. */
    onRemove?: () => void;
}

const RecordedAudioPlayer: React.FC<RecordedAudioPlayerProps> = ({
    audioUrl,
    fileName = 'recording.wav',
    onRemove,
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const playerRef = useRef<ReactHowler>(null);

    useEffect(() => {
        let animationFrame: number | null = null;

        const updateTime = () => {
            if (isPlaying && playerRef.current?.howler) {
                const howler = playerRef.current.howler;
                const newTime = howler.seek();
                setCurrentTime((prev) => {
                    const diff = Math.abs(prev - (newTime as number));
                    return diff > 0.1 ? (newTime as number) : prev;
                });
                animationFrame = requestAnimationFrame(updateTime);
            }
        };

        if (isPlaying) updateTime();

        return () => { if (animationFrame) cancelAnimationFrame(animationFrame); };
    }, [isPlaying]);

    const handleLoad = () => {
        const howler = playerRef.current?.howler;
        if (howler) setDuration(howler.duration());
    };

    const handleEnd = () => { setIsPlaying(false); setCurrentTime(0); };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatTime = (seconds: number): string => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <Card style={{ padding: '16px 20px' }}>
            <ReactHowler
                src={audioUrl}
                playing={isPlaying}
                volume={1.0}
                onLoad={handleLoad}
                onEnd={handleEnd}
                ref={playerRef}
                html5={true}
                format={['wav']}
            />

            <div className="d-flex align-items-center gap-3">
                {/* Play/Pause */}
                <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(var(--bs-primary-rgb), 0.28)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                    {isPlaying
                        ? <Pause size={18} color="white" fill="white" />
                        : <Play size={18} color="white" fill="white" style={{ marginLeft: 2 }} />
                    }
                </button>

                {/* Progress section */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1f36', marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fileName}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '11px', color: '#90a0b7', minWidth: 36, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTime(currentTime)}
                        </span>
                        {/* Progress track */}
                        <div style={{ flex: 1, minWidth: 0, height: 5, borderRadius: 3, background: 'rgba(var(--bs-primary-rgb), 0.10)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{
                                position: 'absolute', top: 0, left: 0, height: '100%',
                                width: `${progress}%`,
                                background: 'linear-gradient(90deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                                borderRadius: 3,
                                transition: 'width 0.1s linear',
                            }} />
                        </div>
                        <span style={{ fontSize: '11px', color: '#90a0b7', minWidth: 36, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                            {formatTime(duration)}
                        </span>
                    </div>
                </div>

                {/* Download */}
                <button
                    onClick={handleDownload}
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'rgba(var(--bs-primary-rgb), 0.06)',
                        border: '1px solid rgba(var(--bs-primary-rgb), 0.14)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.18s ease',
                        flexShrink: 0,
                        color: '#90a0b7',
                        padding: 0,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.12)'; e.currentTarget.style.color = 'var(--bs-primary)'; e.currentTarget.style.transform = 'scale(1.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.06)'; e.currentTarget.style.color = '#90a0b7'; e.currentTarget.style.transform = 'scale(1)'; }}
                    title="Download audio file"
                >
                    <Download size={15} />
                </button>

                {/* Discard */}
                {onRemove && (
                    <button
                        onClick={onRemove}
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            background: 'rgba(220, 38, 38, 0.06)',
                            border: '1px solid rgba(220, 38, 38, 0.16)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.18s ease',
                            flexShrink: 0,
                            color: '#DC2626',
                            padding: 0,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(220, 38, 38, 0.12)'; e.currentTarget.style.transform = 'scale(1.06)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(220, 38, 38, 0.06)'; e.currentTarget.style.transform = 'scale(1)'; }}
                        title="Discard recording"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
        </Card>
    );
};

export default RecordedAudioPlayer;
